import React from "react";
import { API_BASE_URL } from "../../helpers/constants";

const styles = {
  endpoint: {
    marginBottom: "8px",
    padding: "8px",
    border: "1px solid #d4cfc4",
    backgroundColor: "#fbfaf7",
  },
  endpointHeader: {
    cursor: "pointer",
    userSelect: "none",
  },
  endpointTitle: {
    fontSize: "12px",
    fontWeight: 600,
    fontFamily: "monospace",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  endpointDescription: {
    fontSize: "10px",
    color: "#6f6a60",
    marginTop: "2px",
    lineHeight: 1.35,
  },
  endpointBody: {
    marginTop: "8px",
    paddingTop: "8px",
    borderTop: "1px solid #e4e0d9",
  },
  inputRow: {
    display: "flex",
    alignItems: "center",
    marginBottom: "4px",
  },
  label: {
    fontSize: "10px",
    color: "#524b42",
    minWidth: "104px",
    fontWeight: "500",
  },
  input: {
    padding: "3px 6px",
    fontSize: "11px",
    border: "1px solid #d0cbc2",
    flex: 1,
    fontFamily: "monospace",
    backgroundColor: "#fff",
    borderRadius: 0,
  },
  executeButton: {
    padding: "3px 8px",
    fontSize: "11px",
    border: "1px solid #9a8364",
    backgroundColor: "#fff",
    cursor: "pointer",
    marginTop: "6px",
    color: "#6b4e2d",
    borderRadius: 0,
  },
  curlBox: {
    background: "#f4f2ed",
    padding: "6px",
    whiteSpace: "pre-wrap",
    marginBottom: "0",
    fontSize: "10px",
    fontFamily: "monospace",
    border: "1px solid #e0dbd3",
    color: "#5a5449",
    lineHeight: 1.35,
    borderRadius: 0,
  },
};

function CurlBox({
  action,
  url,
  headers,
  body,
  onExecuteCurl,
  copyToClipboard,
}) {
  const [isEditing, setIsEditing] = React.useState(false);
  const [editedCurl, setEditedCurl] = React.useState("");

  const generateCurl = (method, url, headers = {}, body = null) => {
    const headerStrings = Object.entries(headers)
      .map(([k, v]) => `-H "${k}: ${v}"`)
      .join(" \\\n  ");
    const bodyString = body ? `-d '${JSON.stringify(body)}'` : "";
    return `curl -X ${method.toUpperCase()} "${url}" \\\n  ${headerStrings}${bodyString ? " \\\n  " + bodyString : ""}`.trim();
  };

  const curlCommand = generateCurl(action, url, headers, body);

  React.useEffect(() => {
    setEditedCurl(curlCommand);
  }, [curlCommand]);

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditedCurl(curlCommand);
  };

  const handleExecute = () => {
    if (onExecuteCurl) {
      onExecuteCurl(editedCurl);
    }
  };

  return (
    <div style={{ marginTop: "8px", position: "relative" }}>
      <div
        style={{
          position: "absolute",
          top: "4px",
          right: "8px",
          display: "flex",
          gap: "4px",
          zIndex: 1,
        }}
      >
        {!isEditing && (
          <>
            <button
              onClick={() => copyToClipboard(curlCommand)}
              style={{
                fontSize: "8px",
                cursor: "pointer",
                border: "1px solid #ddd",
                background: "#fff",
                borderRadius: "2px",
                padding: "2px 6px",
              }}
              title="Copy to clipboard"
            >
              [_]
            </button>
            <button
              onClick={handleEdit}
              style={{
                fontSize: "10px",
                cursor: "pointer",
                border: "1px solid #ddd",
                background: "#fff",
                borderRadius: "2px",
                padding: "2px 6px",
              }}
            >
              edit
            </button>
          </>
        )}
        {isEditing && (
          <>
            <button
              onClick={handleExecute}
              style={{
                fontSize: "10px",
                cursor: "pointer",
                border: "1px solid #4caf50",
                background: "#4caf50",
                color: "#fff",
                borderRadius: "2px",
                padding: "2px 8px",
                fontWeight: "bold",
              }}
            >
              execute
            </button>
            <button
              onClick={handleCancel}
              style={{
                fontSize: "10px",
                cursor: "pointer",
                border: "1px solid #ddd",
                background: "#fff",
                borderRadius: "2px",
                padding: "2px 6px",
              }}
            >
              cancel
            </button>
          </>
        )}
      </div>
      {isEditing ? (
        <textarea
          value={editedCurl}
          onChange={(e) => setEditedCurl(e.target.value)}
          style={{
            ...styles.curlBox,
            width: "100%",
            minHeight: "100px",
            resize: "vertical",
            fontFamily: "monospace",
            fontSize: "11px",
            padding: "8px",
            paddingTop: "28px",
          }}
        />
      ) : (
        <pre style={{ ...styles.curlBox, paddingTop: "28px" }}>
          {curlCommand}
        </pre>
      )}
    </div>
  );
}

function InputField({ placeholder, value, onChange }) {
  return (
    <div style={styles.inputRow}>
      <div style={styles.label}>{placeholder}</div>
      <input
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
          }
        }}
        style={styles.input}
      />
    </div>
  );
}

function AdminEndpoint({
  id,
  method,
  path,
  description,
  isExpanded,
  onToggle,
  onExecute,
  onExecuteCurl,
  copyToClipboard,
  inputs = [],
  headers = {},
  body = null,
  urlParams = {},
}) {
  const buildUrl = () => {
    let url = `${API_BASE_URL}${path}`;
    // Replace :param placeholders with actual values
    Object.entries(urlParams).forEach(([key, value]) => {
      url = url.replace(`:${key}`, value || `:${key}`);
    });
    return url;
  };

  return (
    <div style={styles.endpoint}>
      <div style={styles.endpointHeader} onClick={() => onToggle(id)}>
        <div style={styles.endpointTitle}>
          <span>
            {method} {path}
          </span>
          <span style={{ fontSize: "16px" }}>{isExpanded ? "−" : "+"}</span>
        </div>
        <div style={styles.endpointDescription}>{description}</div>
      </div>
      {isExpanded && (
        <div style={styles.endpointBody}>
          {inputs.map((input, idx) => (
            <InputField
              key={idx}
              placeholder={input.placeholder}
              value={input.value}
              onChange={input.onChange}
            />
          ))}
          <CurlBox
            action={method}
            url={buildUrl()}
            headers={headers}
            body={body}
            onExecuteCurl={onExecuteCurl}
            copyToClipboard={copyToClipboard}
          />
          <button onClick={onExecute} style={styles.executeButton}>
            →
          </button>
        </div>
      )}
    </div>
  );
}

export default AdminEndpoint;
